import { APIError, ClientError, useErrorHandler, useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { updateServer } from 'api/servers/servers';
import { Server } from 'api/servers/servers.types';
import { PaginatedResponse } from 'core/global.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';

import { ServerDetailsEditContainerProps } from './ServerDetailsEdit.types';
import { ServerDetailsEdit } from './ServerDetailsEdit';
import { useServerDetailsEditValidation } from './ServerDetailsEdit.validation';

export const ServerDetailsEditContainer = ({ selectedServer, servers }: ServerDetailsEditContainerProps) => {
  const { formatMessage } = useLocale();
  const { handleError } = useErrorHandler();
  const queryClient = useQueryClient();

  const { schema } = useServerDetailsEditValidation(selectedServer, servers);

  const formMethods = useForm<Server>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: selectedServer,
  });

  const { mutate: onUpdateServer, isLoading } = useMutation(updateServer, {
    onSuccess: (resServer) => {
      queryClient.setQueryData<PaginatedResponse<Server> | undefined>([QueryKeyEnum.SERVERS], (current) => {
        if (!current) return;

        toast.dark(formatMessage({ id: AppMessages['devices.details.edit.success'] }));

        return {
          ...current,
          data: current.data.map((server) => (selectedServer.id === server.id ? resServer : server)),
        };
      });
    },
    onError: (error: ClientError<APIError>) => {
      const errorFields = error.response?.data.fields;

      const isNameExistsError = errorFields?.find(({ type, field }) => type === 'exists' && field === 'nazwa');

      if (isNameExistsError) {
        return formMethods.setError('nazwa', {
          message: formatMessage({ id: AppMessages['devices.details.edit.nameExists'] }),
        });
      }

      handleError({ error, omitAPIErrorMessage: true });
    },
  });

  const onSubmit = (body: Server) => {
    onUpdateServer({
      id: selectedServer.id,
      body: { ...body, port: Number(body.port), timeout: Number(body.timeout) },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <ServerDetailsEdit selectedServer={selectedServer} isLoading={isLoading} onSubmit={onSubmit} />
    </FormProvider>
  );
};
